import { useMemo, useState } from 'react';
import {
  rememberedSort,
  useListMemory,
  usePruneIds,
  useRememberEffect,
  useRememberedString,
} from '../list-memory';
import {
  INVOICE_STATUSES,
  formatMoney,
  formatSort,
  invoiceSchema,
  invoiceSummarySchema,
  type Invoice,
  type InvoiceStatus,
} from '@forecastify/shared';
import {
  DataList,
  type DataListColumn,
  type DataListRow,
  type ListFilter,
  type MenuItem,
  type SortState,
} from '@forecastify/ui';
import { useT } from '../../i18n';
import { listLabels } from '../leave/requests-screen';
import { useServerList, useServerSummary } from '../use-server-list';
import { personCell } from '../person-cell';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A számlák táblája — C57. One component, two screens.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE WORKSPACE LIST AND THE PROJECT TAB DIFFER BY ONE COLUMN AND ONE FILTER**, which the
 * mockup makes explicit: `invColumnsAll` is `invColumnsProj` with *Projekt* spliced in after the
 * number, and `invFiltersAll` is `invFiltersProj` plus the project picker. Two components would be
 * two places for the status vocabulary and the footer arithmetic to drift.
 *
 * ⚠️ **`DataList`, like every other table in this product** — the 15 Sep ruling, and what the
 * mockup draws here anyway.
 *
 * ⚠️ **31 Oct 2026 · one page at a time, from the server.** Search, the tabs, both filters and the
 * sort are query parameters, and the tabs' counts are `/invoices/summary`'s — a count of one page
 * would be a number about rows nobody can see. `nonce` re-reads the page after a write.
 */

/** ⚠️ The mockup's own `INV_ST` colours, for the dot on a status menu item. */
const STATUS_DOT: Record<InvoiceStatus, string> = {
  draft: 'var(--status-idle)',
  issued: 'var(--status-info)',
  paid: 'var(--status-ok)',
  void: 'var(--status-idle)',
};

export function statusLabel(t: ReturnType<typeof useT>, status: InvoiceStatus): string {
  /* ⚠️ One literal key per branch — a computed key is invisible to `i18n:check`. */
  if (status === 'draft') return t('invoice.status.draft');
  if (status === 'issued') return t('invoice.status.issued');
  if (status === 'paid') return t('invoice.status.paid');
  return t('invoice.status.void');
}

/** `Sortétel · Chat kontextuskezelés` / `Időszak · cont_12a3cd5a #6` — the mockup's two shapes. */
export function targetLabel(t: ReturnType<typeof useT>, invoice: Invoice): string {
  if (invoice.targetKind === 'line_item') {
    return `${t('invoice.target.lineItem')} · ${invoice.lineItemName ?? t('invoice.target.gone')}`;
  }
  const code = invoice.contractIdentifier ?? ''; /* R-57 */
  const index = invoice.occurrenceIndex;
  const period = index === null ? (invoice.occurrenceStartsOn ?? '') : `#${String(index)}`;
  return `${t('invoice.target.occurrence')} · ${`${code} ${period}`.trim()}`;
}

export interface InvoiceListHandlers {
  readonly canManage: boolean;
  readonly onOpen: (invoice: Invoice) => void;
  readonly onEdit: (invoice: Invoice) => void;
  readonly onStatus: (invoice: Invoice, status: InvoiceStatus) => void;
  readonly onDelete: (invoice: Invoice) => void;
  /** Absent where there is nothing sensible to preselect — the workspace list, for one. */
  readonly onNew?: () => void;
}

export function InvoiceList({
  projectId,
  nonce,
  currencies,
  projects,
  handlers,
}: {
  /** The project tab's project — its invoices and no others. Absent on the workspace list. */
  readonly projectId?: string;
  /** `useInvoices`'s `revision` — every write and every live change re-reads the page. */
  readonly nonce: number;
  readonly currencies: readonly {
    readonly code: string;
    readonly symbol: string;
    readonly rateToBase: string;
  }[];
  /** ⚠️ Given, the *Projekt* column and its filter appear — this is the workspace list. */
  readonly projects?: readonly { readonly id: string; readonly name: string }[];
  readonly handlers: InvoiceListHandlers;
}) {
  const t = useT();
  /* 3 Nov 2026 · remembered per workspace and member — the workspace list, and a project's tab
     as its own list (`list-memory.ts`). */
  const memory = useListMemory(projectId === undefined ? 'invoices' : 'project-invoices');
  const [query, setQuery] = useRememberedString(memory, 'search');
  const [sort, setSort] = useState<readonly SortState[]>(() => rememberedSort(memory.restored));
  useRememberEffect(memory, { sort });
  const [view, setView] = useRememberedString(memory, 'view', 'all');
  const [projectFilter, setProjectFilter] = useRememberedString(memory, 'projectId');
  const [targetFilter, setTargetFilter] = useRememberedString(memory, 'target');
  /* Rule 4 · a remembered project no longer offered goes once the list arrives. */
  const knownProjects = useMemo(
    () =>
      projects === undefined || projects.length === 0
        ? null
        : new Set(projects.map((one) => one.id)),
    [projects],
  );
  usePruneIds(projectFilter, knownProjects, setProjectFilter);

  const scoped = projects !== undefined;

  const money = (amount: string, code: string): string => {
    const symbol = currencies.find((one) => one.code === code)?.symbol ?? code;
    return formatMoney(Number(amount), { code, symbol });
  };

  /* The filters as the server reads them (`invoiceFilterOf`). */
  const filtered = {
    projectId: projectId ?? projectFilter,
    target: targetFilter,
    search: query.trim(),
  };
  const list = useServerList({
    path: '/invoices',
    params: {
      ...filtered,
      status: view === 'all' ? '' : view,
      sort: formatSort(sort) ?? '',
    },
    schema: invoiceSchema,
    nonce,
  });
  const invoices = list.rows;
  /* The tabs' counts — every filter but the tab's own status. */
  const summary = useServerSummary({
    path: '/invoices/summary',
    params: filtered,
    schema: invoiceSummarySchema,
    nonce,
  });

  const columns: DataListColumn[] = [
    { key: 'number', label: t('list.identifier'), width: 120, sortKey: 'number' },
    {
      key: 'externalId',
      label: t('invoice.column.externalId'),
      width: 150,
      sortKey: 'externalId',
    },
    ...(scoped
      ? [
          {
            key: 'project',
            label: t('invoice.column.project'),
            width: 160,
            sortKey: 'project',
          },
        ]
      : []),
    { key: 'note', label: t('invoice.column.note'), sortKey: 'note' },
    { key: 'date', label: t('invoice.column.date'), width: 100, sortKey: 'date' },
    {
      key: 'amount',
      label: t('invoice.column.amount'),
      width: 126,
      align: 'right' as const,
      sortKey: 'amount',
    },
    { key: 'target', label: t('invoice.column.target'), width: 220, sortKey: 'target' },
    { key: 'status', label: t('invoice.column.status'), width: 112, sortKey: 'status' },
    { key: 'owner', label: t('invoice.column.owner'), width: 150, sortKey: 'owner' },
  ];

  /**
   * ⚠️⚠️ **IN THE SERVER'S ORDER, SORTED FROM THE ROW'S OWN FIELDS** (`INVOICE_SORT`). A cell
   * carries formatted text — money with a separator — and ordering that sorts the amounts as text.
   */
  const rows: DataListRow[] = invoices.map((invoice) => ({
    id: invoice.id,
    cells: {
      number: {
        text: invoice.identifier,
        mono: true,
        muted: true,
        sort: invoice.identifier,
      },
      externalId: { text: invoice.externalId, mono: true, bold: true, sort: invoice.externalId },
      ...(scoped
        ? {
            project: {
              text: invoice.projectName,
              muted: true,
              avatar: {
                initials: '',
                color: invoice.projectColor ?? 'var(--status-idle)',
                title: invoice.projectName,
              },
            },
          }
        : {}),
      note: { text: invoice.note === '' ? '–' : invoice.note, muted: true },
      date: { text: invoice.issuedOn, mono: true, sort: invoice.issuedOn },
      amount: {
        text: money(invoice.amount, invoice.currencyCode),
        mono: true,
        bold: true,
        sort: Number(invoice.amount),
      },
      target: { text: targetLabel(t, invoice) },
      status: {
        badge: {
          label: statusLabel(t, invoice.status),
          /* ⚠️ The mockup's own mapping: `paid` reads settled, `issued` is the live one that wants
             attention, and `draft`/`void` are both outlines because neither is a commitment. */
          variant:
            invoice.status === 'paid'
              ? ('secondary' as const)
              : invoice.status === 'issued'
                ? ('default' as const)
                : ('outline' as const),
        },
      },
      owner: personCell(invoice.ownerName, { seed: invoice.ownerAvatarSeed }),
    },
  }));

  const filters: ListFilter[] = [
    ...(projects === undefined
      ? []
      : [
          {
            key: 'project',
            /* 3 Nov 2026 · several at once; no "all" row — nothing ticked is all. */
            kind: 'multi' as const,
            label: t('invoice.filter.project'),
            value: projectFilter,
            options: projects.map((one) => ({ value: one.id, label: one.name })),
            onChange: setProjectFilter,
          },
        ]),
    {
      key: 'target',
      kind: 'multi',
      label: t('invoice.column.target'),
      value: targetFilter,
      options: [
        { value: 'line_item', label: t('invoice.target.lineItem') },
        { value: 'occurrence', label: t('invoice.target.occurrence') },
      ],
      onChange: setTargetFilter,
    },
  ];

  const rowMenu = (id: string): MenuItem[] => {
    const invoice = invoices.find((one) => one.id === id);
    if (invoice === undefined) return [];
    const all: MenuItem[] = [
      {
        label: t('invoice.action.open'),
        icon: 'arrow-right',
        onSelect: () => {
          handlers.onOpen(invoice);
        },
      },
      {
        label: t('invoice.action.edit'),
        icon: 'pencil',
        onSelect: () => {
          handlers.onEdit(invoice);
        },
      },
      { type: 'separator' },
      { type: 'label', label: t('invoice.column.status') },
      ...INVOICE_STATUSES.map((status) => ({
        label: statusLabel(t, status),
        dot: STATUS_DOT[status],
        checked: invoice.status === status,
        onSelect: () => {
          handlers.onStatus(invoice, status);
        },
      })),
      { type: 'separator' },
      {
        label: t('invoice.action.delete'),
        icon: 'trash',
        destructive: true,
        onSelect: () => {
          handlers.onDelete(invoice);
        },
      },
    ];
    /* 22 Oct 2026 · an invoice on a project the reader does not manage opens, and that is all. */
    return invoice.canManage ? all : all.slice(0, 1);
  };

  return (
    <DataList
      columnsKey="invoices"
      card={{
        title: 'number',
        context: 'project',
        defaults: {
          badge: 'status',
          avatar: 'owner',
          details: ['amount', 'date', 'target', 'externalId'],
        },
      }}
      isLoading={list.loading}
      columns={columns}
      rows={rows}
      page={list.page}
      sort={sort}
      onSortChange={setSort}
      /* ⚠️ The toolbar's placeholder IS `labels.search` — there is no separate prop, and the
         mockup's own wording here names what the field matches. */
      labels={{ ...listLabels(t), search: t('invoice.search') }}
      search={query}
      onSearchChange={setQuery}
      filters={filters}
      views={[
        {
          value: 'all',
          label: t('invoice.view.all'),
          ...(summary === null ? {} : { count: summary.total }),
        },
        ...INVOICE_STATUSES.map((status) => ({
          value: status,
          label: statusLabel(t, status),
          ...(summary === null ? {} : { count: summary.byStatus[status] }),
        })),
      ]}
      view={view}
      onViewChange={setView}
      onRowClick={(id) => {
        const invoice = invoices.find((one) => one.id === id);
        if (invoice !== undefined) handlers.onOpen(invoice);
      }}
      {...(handlers.canManage ? { rowMenu } : {})}
      actions={
        handlers.onNew === undefined || !handlers.canManage
          ? []
          : [{ label: t('invoice.new'), icon: 'plus', primary: true, onSelect: handlers.onNew }]
      }
      empty={{
        icon: 'file-text',
        title: t('invoice.empty'),
        /* ⚠️ The mockup's own sentence, which says where invoices come FROM — there is no
         *new invoice* from nowhere: one is raised on the deliverable it bills. */
        body: t('invoice.emptyBody'),
      }}
    />
  );
}
